import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useContext } from 'react';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { Box, Button, Flex, Grid } from '@chakra-ui/react';
import { EventEmitter, hasValue, useT } from '@progress-fe/core';
import { JsFieldName, JsSelectNode, JsFieldInspector } from '../../jsFormBase';
import { JsFormConfigContext } from '../../../contexts';
import { Draggable } from './components';
const NULLABLE_ITEM_ID = 'unknown-id';
/**
 * Component renders "/schemas/jsf-connections" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of TNodeHandleFormData[].
 */
const ConnectionsJsFieldFC = (props) => {
    const { isInspector } = useContext(JsFormConfigContext) ?? {};
    const formData = structuredClone(props.formData);
    const { t } = useT();
    const handlePropsOnChange = (newFormData) => {
        props.onChange(newFormData);
    };
    const handleChange = (index, valueIndex, option) => {
        const formDataCopy = structuredClone(formData);
        const portOption = formDataCopy[index].options.find((o) => o.id === option.value);
        if (portOption) {
            formDataCopy[index].values[valueIndex] = { id: portOption.id, portCode: portOption.portCode };
            handlePropsOnChange(formDataCopy);
        }
    };
    const handleEmptyConnection = (targetIndex) => {
        const formDataCopy = [...formData];
        formDataCopy[targetIndex].values.push(null);
        handlePropsOnChange(formDataCopy);
    };
    const handleDeleteConnection = (index, valueIndex) => {
        const formDataCopy = structuredClone(formData);
        formDataCopy[index].values.splice(valueIndex, 1);
        handlePropsOnChange(formDataCopy);
    };
    const handleClearConnection = (index, valueIndex) => {
        const formDataCopy = structuredClone(formData);
        formDataCopy[index].values[valueIndex] = null;
        handlePropsOnChange(formDataCopy);
    };
    const handlePickElement = (elementId) => {
        EventEmitter.emit('PickItem', elementId);
    };
    const handleSelectElement = (elementId) => {
        EventEmitter.emit('SelectItem', elementId);
    };
    const handleDragEnd = (event, index) => {
        const from = event.active.data.current?.sortable.index;
        const to = event.over?.data.current?.sortable.index;
        if (hasValue(from) && hasValue(to) && from !== to) {
            const formDataCopy = structuredClone(formData);
            const valuesByIndex = structuredClone(formDataCopy[index].values);
            formDataCopy[index].values = arrayMove(valuesByIndex, from, to);
            handlePropsOnChange(formDataCopy);
        }
    };
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['array'], children: _jsx(Box, { pb: '1px', "data-testid": "ConnectionsJsField-test", "data-name": props.name, children: _jsx(Flex, { flexDirection: "column", gap: "4px", children: formData.length === 0 ? (_jsx(Box, { borderRadius: "4px", border: "1px solid", borderColor: "border", children: _jsx(Flex, { h: "64px", alignItems: "center", justifyContent: "center", children: t('connections.noConnections') }) })) : (_jsx(_Fragment, { children: formData.map((item, index) => {
                        const alreadySelected = item.values.filter((v) => !!v).map((v) => v?.id);
                        const options = item.options.map((opt) => ({
                            value: opt.id,
                            label: opt.elementName
                        }));
                        const filteredOptions = options.filter((opt) => !alreadySelected.includes(opt.value));
                        return (_jsxs(Grid, { gridTemplateColumns: "minmax(0, 180px) 1fr", gap: "4px", children: [_jsx(Box, { p: "3px 0 0 0", children: _jsx(JsFieldName, { name: item.name }) }), _jsx(Box, { children: item.capacitance === 1 ? (_jsx(JsSelectNode, { name: item.name, options: filteredOptions, isDisabled: props.schema.readOnly, isDeleteDisabled: !item.values[0], selected: options.find((o) => o.value === item.values[0]?.id), onChange: (selected) => handleChange(index, 0, selected), onDelete: () => handleClearConnection(index, 0), onSelect: handleSelectElement, onPick: handlePickElement })) : (_jsxs(Flex, { flexDirection: "column", gap: "4px", children: [_jsx(DndContext, { modifiers: [restrictToVerticalAxis], onDragEnd: (e) => handleDragEnd(e, index), children: _jsx(SortableContext, { disabled: props.schema.readOnly, items: item.values.map((value) => !value ? { id: NULLABLE_ITEM_ID, portCode: '' } : value), children: item.values.map((valueItem, valueIndex) => (_jsx(Draggable, { id: valueItem?.id ?? NULLABLE_ITEM_ID, isDisabled: props.schema.readOnly, isActive: item.isOrdering, children: _jsx(JsSelectNode, { options: filteredOptions, name: `${item.name}${valueIndex}`, selected: options.find((o) => o.value === valueItem?.id), isDisabled: props.schema.readOnly, onChange: (selected) => handleChange(index, valueIndex, selected), onDelete: () => handleDeleteConnection(index, valueIndex), onSelect: handleSelectElement, onPick: handlePickElement }) }, valueItem?.id ?? NULLABLE_ITEM_ID))) }) }), !props.schema.readOnly && item.values.length < item.capacitance && (_jsx(Button, { width: "100%", size: "smSquare", variant: "ghost", isDisabled: item.values.some((v) => v === null), onClick: () => handleEmptyConnection(index), children: t('actions.add') }))] })) })] }, item.name));
                    }) })) }) }) }));
};
export const ConnectionsJsField = memo(ConnectionsJsFieldFC);
//# sourceMappingURL=ConnectionsJsField.js.map